/**
 * AK-938 — карточка опыта (low-fi 4·А, 5·Б, 5·В): карта вариантов, варианты с
 * урожайностью и прибавкой, лента операций и осмотров, закрытие.
 *
 * Права D8 (ADR-086): опыт консультанта на поле клиента меняет и закрывает только
 * заложившая организация; хозяин поля добавляет свои осмотры и операции. Кнопку
 * «Закрыть» хозяину такого опыта не показываем — сервер всё равно ответит 403.
 * «Отчёт клиенту» (PDF) — после демо: кнопка неактивна с подсказкой.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, FileText, Tractor } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FieldContourMiniMap } from '@/components/map/FieldContourMiniMap';
import { zoneVisual } from '@/components/fields/card/geometryLogic';
import { useFieldContourGeo } from '@/components/fields/card/widgets/widgetApi';
import type { FieldDetail } from '@/components/fields/FieldOverviewTab';
import { InspectionCreateSheet } from '@/components/fields/inspections/InspectionCreateSheet';
import { CloseTrialSheet } from '@/components/science/CloseTrialSheet';
import { TrialOperationDialog } from '@/components/science/TrialOperationDialog';
import { TrialStatusBadge } from '@/components/science/TrialStatusBadge';
import { api } from '@/lib/api';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import { cn } from '@/lib/utils';
import { scienceKeys, useTrialSummary } from '@/lib/science/api';
import {
  formatCHa,
  formatHa,
  isClosed,
  variantMarks,
  variantTreatment,
} from '@/lib/science/labels';
import { pickFieldContour } from '@/lib/science/placement';
import { useScienceSeasonLensHost } from '@/lib/science/seasonLens';
import type { TrialEvent } from '@/lib/science/types';

/** Скрытых слоёв нет; один экземпляр, чтобы мини-карта не перерисовывала слои на каждый рендер. */
const NO_HIDDEN: Set<string> = new Set();

const EVENT_KIND: Record<TrialEvent['kind'], string> = {
  sowing: 'Посев',
  inspection: 'Осмотр',
  operation: 'Тех.операция',
  harvest: 'Уборка',
};

function eventTitle(e: TrialEvent): string {
  const phase = e.phase === 'before' ? ' · до внесения' : e.phase === 'after' ? ' · после внесения' : '';
  const kind = EVENT_KIND[e.kind];
  const title = e.title && e.title !== kind ? `${kind} · ${e.title}` : kind;
  return `${title}${phase}`;
}

export function TrialCardPage({ fieldId, trialId }: { fieldId: string; trialId: string }) {
  useScienceSeasonLensHost();
  const qc = useQueryClient();
  const summary = useTrialSummary(fieldId, trialId);
  const contours = useFieldContourGeo(fieldId);
  const field = useQuery({
    queryKey: ['science', 'field-detail', fieldId],
    queryFn: () => api.get<FieldDetail>(`/api/v1/fields/${fieldId}/detail`),
    staleTime: 60_000,
  });
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [opOpen, setOpOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const t = summary.data;
  const marks = useMemo(() => variantMarks((t?.variants ?? []).map((v) => v.variant_role)), [t]);
  const contour = useMemo(() => pickFieldContour(contours.data), [contours.data]);
  const variantZones = useMemo(
    () =>
      (t?.variants ?? []).map((v, i) => ({
        id: v.subzone_id,
        geojson: v.geojson ?? null,
        kind: 'trial',
        index: i,
      })),
    [t],
  );
  const fieldLabel = field.data?.name ?? 'Поле';
  const closed = isClosed(t?.status);
  const canManage = !!t && !t.placed_by_other_org && !closed;
  const events = useMemo(
    () => [...(t?.events ?? [])].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
    [t],
  );

  const meta = [
    field.data?.farm_name,
    t?.variants.find((v) => v.sowing?.crop_name)?.sowing?.crop_name,
    t?.laid_at ? `заложен ${formatRuDateNumeric(t.laid_at)}` : null,
    t ? `сезон ${t.season_year}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const rail = t ? (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Варианты</CardTitle>
        <p className="text-sm text-muted-foreground">урожайность и прибавка к контролю</p>
      </CardHeader>
      <CardContent className="overflow-x-auto p-4 pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Вариант</TableHead>
              <TableHead className="text-right">Га</TableHead>
              <TableHead className="text-right">ц/га</TableHead>
              <TableHead className="text-right">к контролю</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {t.variants.map((v, i) => (
              <TableRow key={v.subzone_id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn('h-3 w-3 shrink-0 rounded-sm', zoneVisual('trial', i).bg)}
                      aria-hidden
                    />
                    <span className="font-medium">
                      {marks[i]} · {v.label}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {variantTreatment(v.inputs, v.treatment_note)}
                  </div>
                </TableCell>
                <TableCell className="text-right">{formatHa(v.area_ha).replace(' га', '')}</TableCell>
                <TableCell className="text-right">{formatCHa(v.yield_c_ha)}</TableCell>
                <TableCell className="text-right">
                  {v.variant_role === 'control' ? '—' : formatCHa(v.yield_delta_c_ha, true)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  ) : null;

  return (
    <AppShell>
      <Stack gap={6}>
        <PageHeader
          title={t ? `${fieldLabel} · ${t.name}` : 'Опыт'}
          description={meta || undefined}
          breadcrumbs={[{ label: 'Наука', to: '/science' }, { label: 'Опыт' }]}
          actions={
            t ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" disabled title="Отчёт клиенту в PDF — после демо">
                  <FileText className="h-4 w-4" />
                  Отчёт клиенту · после демо
                </Button>
                {!closed && (
                  <>
                    <Button variant="outline" onClick={() => setInspectionOpen(true)}>
                      <ClipboardCheck className="h-4 w-4" />
                      Добавить осмотр
                    </Button>
                    <Button variant="outline" onClick={() => setOpOpen(true)}>
                      <Tractor className="h-4 w-4" />
                      Добавить операцию
                    </Button>
                  </>
                )}
                {canManage && (
                  <Button onClick={() => setCloseOpen(true)}>
                    <CheckCircle2 className="h-4 w-4" />
                    Закрыть опыт
                  </Button>
                )}
              </div>
            ) : undefined
          }
        />

        {summary.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : summary.isError || !t ? (
          <Alert variant="destructive">
            <AlertDescription>
              Опыт открыть не удалось{summary.error ? `: ${summary.error.message}` : ''}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <TrialStatusBadge status={t.status} label={t.status_label} />
              {t.placed_by_other_org && (
                <Badge variant="outline">заложил консультант — вы добавляете осмотры и операции</Badge>
              )}
              {t.origin_scheme_id && !t.placed_by_other_org && (
                <Link
                  to="/science/schemes/$schemeId"
                  params={{ schemeId: t.origin_scheme_id }}
                  className="text-sm text-primary underline-offset-4 hover:underline"
                >
                  Схема опыта{t.origin_scheme_version ? ` · версия ${t.origin_scheme_version}` : ''}
                </Link>
              )}
              <Link
                to="/fields/$fieldId"
                params={{ fieldId }}
                className="text-sm text-primary underline-offset-4 hover:underline"
              >
                Карточка поля
              </Link>
            </div>

            {t.status === 'closed_incomplete' && (
              <Alert>
                <AlertDescription>
                  Закрыт как недоделанный{t.closed_at ? ` ${formatRuDateNumeric(t.closed_at)}` : ''}:{' '}
                  {t.close_note || 'причина не указана'}. В сводку урожайности по схеме не входит.
                </AlertDescription>
              </Alert>
            )}
            {t.warnings.map((w) => (
              <Alert key={w}>
                <AlertDescription>{w}</AlertDescription>
              </Alert>
            ))}

            <PageWithRail rail={rail}>
              <div className="relative aspect-[4/3] min-h-[20rem] w-full overflow-hidden rounded-xl border border-border bg-muted">
                {contours.isLoading ? (
                  <Skeleton className="absolute inset-0 h-full w-full" />
                ) : (
                  <FieldContourMiniMap
                    fieldId={fieldId}
                    farmId={field.data?.farm_id}
                    referenceGeo={contour}
                    zones={variantZones}
                    hidden={NO_HIDDEN}
                    referenceHidden={false}
                    editing={false}
                    onEditingChange={() => {}}
                    onSaveGeometry={async () => {}}
                  />
                )}
              </div>
              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-base">Лента</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    операции и осмотры по вариантам и по всему полю, новые сверху
                  </p>
                </CardHeader>
                <CardContent className="overflow-x-auto p-4 pt-0">
                  {events.length ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Дата</TableHead>
                          <TableHead>Событие</TableHead>
                          <TableHead>Варианты</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {events.map((e) => (
                          <TableRow key={`${e.kind}:${e.ref_id}`}>
                            <TableCell className="whitespace-nowrap">
                              {e.date ? formatRuDateNumeric(e.date) : '—'}
                            </TableCell>
                            <TableCell className="font-medium">{eventTitle(e)}</TableCell>
                            <TableCell>{e.scope_label}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Пока пусто. Добавьте осмотр «до внесения» и операцию по вариантам.
                    </p>
                  )}
                </CardContent>
              </Card>
            </PageWithRail>

            <InspectionCreateSheet
              fieldId={fieldId}
              fieldName={fieldLabel}
              open={inspectionOpen}
              onOpenChange={setInspectionOpen}
              variants={t.variants.map((v, i) => ({ id: v.subzone_id, label: `${marks[i]} · ${v.label}` }))}
              onSaved={() => void qc.invalidateQueries({ queryKey: scienceKeys.all })}
            />
            <TrialOperationDialog trial={t} open={opOpen} onOpenChange={setOpOpen} />
            {canManage && (
              <CloseTrialSheet
                trial={t}
                fieldLabel={fieldLabel}
                open={closeOpen}
                onOpenChange={setCloseOpen}
              />
            )}
          </>
        )}
      </Stack>
    </AppShell>
  );
}
